import React from "react";
import { Col, Row } from "react-bootstrap";

import { ACTION_COLORS, CONDITION_TYPES, FILTER_TYPES, NO_ACTION_TYPES, PAGE_SIZES } from "./custom-module-utils";
import { emptyAction, setListSetting, splitOptions } from "./builder-draft";
import { ConditionEditor, Label, RowErrors } from "./builder-fields";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";

const Heading = ({ title, hint }) => (
  <>
    <div className="fs-16 fw-600 text-dkblue mb-1">{title}</div>
    {hint && <div className="fs-13 fw-500 text-grey mb-3">{hint}</div>}
  </>
);

const toggleKey = (keys, key) => (keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]);
const labelOf = (field) => field.label || field.key;

export const ListViewTab = ({ draft, setDraft }) => {
  const { list } = draft.settings;
  // Hidden fields never reach the list page.
  const keyed = draft.fields.filter((field) => field.key && !field.hidden);
  const byKey = Object.fromEntries(keyed.map((field) => [field.key, field]));
  const set = (patch) => setDraft((current) => setListSetting(current, patch));

  // Until columns are picked the list shows the first five fields.
  const columns = (list.columns.length ? list.columns : keyed.slice(0, 5).map((field) => field.key)).filter((key) => byKey[key]);
  const hidden = keyed.filter((field) => !columns.includes(field.key));
  const filterable = keyed.filter((field) => FILTER_TYPES.includes(field.type));
  const boardable = keyed.filter((field) => field.type === "select");

  const moveColumn = (index, step) => {
    const next = [...columns];
    [next[index], next[index + step]] = [next[index + step], next[index]];
    set({ columns: next });
  };

  return (
    <>
      <div className="p-4 mb-3 cm-card">
        <Heading title="Columns" hint="The list page's columns, top to bottom = left to right. The record number, when enabled, always comes first." />
        {columns.map((key, index) => (
          <div key={key} className="d-flex align-items-center justify-content-between p-2 mb-2 cm-field-card">
            <span className="fs-14 fw-600 text-dkblue">{labelOf(byKey[key])}</span>
            <div className="d-flex align-items-center gap-1">
              <button type="button" className="cm-icon-btn" aria-label={`Move ${labelOf(byKey[key])} earlier`} disabled={index === 0} onClick={() => moveColumn(index, -1)}>↑</button>
              <button
                type="button"
                className="cm-icon-btn"
                aria-label={`Move ${labelOf(byKey[key])} later`}
                disabled={index === columns.length - 1}
                onClick={() => moveColumn(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="cm-icon-btn"
                aria-label={`Hide ${labelOf(byKey[key])}`}
                disabled={columns.length === 1}
                onClick={() => set({ columns: columns.filter((item) => item !== key) })}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
        {hidden.length > 0 && (
          <select className="form-control mt-2" style={{ maxWidth: "320px" }} aria-label="Add column" value="" onChange={(e) => e.target.value && set({ columns: [...columns, e.target.value] })}>
            <option value="">+ Add column</option>
            {hidden.map((field) => (
              <option key={field.key} value={field.key}>{labelOf(field)}</option>
            ))}
          </select>
        )}
        {!keyed.length && <div className="fs-13 fw-500 text-grey">Label your fields in Form Layout first.</div>}
      </div>

      <Row>
        <Col md={6}>
          <div className="p-4 mb-3 cm-card">
            <Heading title="Sorting & Paging" />
            <Row>
              <Col sm={7} className="mb-3">
                <Label htmlFor="lv-sort">DEFAULT SORT</Label>
                <select id="lv-sort" className="form-control" value={list.sort_field || "created"} onChange={(e) => set({ sort_field: e.target.value })}>
                  <option value="created">Created date</option>
                  {draft.settings.code_prefix && <option value="code">Record number</option>}
                  {keyed.map((field) => (
                    <option key={field.key} value={field.key}>{labelOf(field)}</option>
                  ))}
                </select>
              </Col>
              <Col sm={5} className="mb-3">
                <Label htmlFor="lv-dir">DIRECTION</Label>
                <select id="lv-dir" className="form-control" value={list.sort_dir} onChange={(e) => set({ sort_dir: e.target.value })}>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </Col>
              <Col sm={7}>
                <Label htmlFor="lv-size">RECORDS PER PAGE</Label>
                <select id="lv-size" className="form-control" value={list.page_size} onChange={(e) => set({ page_size: Number(e.target.value) })}>
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </Col>
            </Row>
          </div>
        </Col>
        <Col md={6}>
          <div className="p-4 mb-3 cm-card">
            <Heading title="Board View" hint="Adds a Kanban board with one column per option of a dropdown field. Dragging a card to another column changes that value." />
            <Label htmlFor="lv-board">GROUP CARDS BY</Label>
            <select id="lv-board" className="form-control" value={list.board_field} onChange={(e) => set({ board_field: e.target.value })}>
              <option value="">Off (list only)</option>
              {boardable.map((field) => (
                <option key={field.key} value={field.key}>{labelOf(field)}</option>
              ))}
            </select>
            {!boardable.length && <div className="fs-12 fw-500 text-grey mt-1">Add a Dropdown field to enable the board.</div>}
          </div>
        </Col>
      </Row>

      <div className="p-4 mb-3 cm-card">
        <Heading title="Filters" hint="Shown above the list. Dropdown, multi-select, checkbox, date and autocomplete fields can be filters." />
        {filterable.length ? (
          <div className="d-flex flex-wrap gap-4">
            {filterable.map((field) => (
              <label key={field.key} className="fs-14 fw-500 text-grey d-flex align-items-center gap-1 mb-0">
                <input type="checkbox" checked={list.filters.includes(field.key)} onChange={() => set({ filters: toggleKey(list.filters, field.key) })} />
                {labelOf(field)}
              </label>
            ))}
          </div>
        ) : (
          <div className="fs-13 fw-500 text-grey">No fields of those types yet.</div>
        )}
      </div>
    </>
  );
};

export const DetailViewTab = ({ draft, setDraft }) => {
  const { view } = draft.settings;
  const set = (patch) => setDraft((current) => ({ ...current, settings: { ...current.settings, view: { ...current.settings.view, ...patch } } }));
  const option = (key, title, hint) => (
    <label className="d-flex align-items-start gap-2 mb-3">
      <input type="checkbox" className="mt-1" checked={view[key]} onChange={(e) => set({ [key]: e.target.checked })} />
      <span>
        <span className="fs-14 fw-600 text-dkblue d-block">{title}</span>
        <span className="fs-13 fw-500 text-grey">{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="p-4 mb-3 cm-card">
      <Heading title="Detail Page" hint="Details are laid out with the same sections and columns as the form; arrange them in Form Layout. Buttons are set up in Buttons." />
      {option("history", "Activity & notes", "A timeline of when the record was created, every field change with its old and new value, button use, and notes your team adds.")}
      {option("related", "Related records", "Records of other custom modules whose Autocomplete field points at this module, with a link to all of them.")}
    </div>
  );
};

export const ButtonsTab = ({ draft, setDraft, errors }) => {
  const update = (index, patch) =>
    setDraft((current) => ({ ...current, actions: current.actions.map((action, i) => (i === index ? { ...action, ...patch } : action)) }));
  const targets = draft.fields.filter((field) => field.key && !NO_ACTION_TYPES.includes(field.type));
  const controllers = draft.fields.filter((field) => field.key && CONDITION_TYPES.includes(field.type));

  const valueInput = (action, index, id) => {
    const field = draft.fields.find((item) => item.key === action.field);
    const onChange = (e) => update(index, { value: e.target.value });
    if (field?.type === "checkbox") {
      return (
        <select id={id} className="form-control" value={String(action.value === true)} onChange={(e) => update(index, { value: e.target.value === "true" })}>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }
    if (field?.type === "select") {
      return (
        <select id={id} className="form-control" value={action.value ?? ""} onChange={onChange}>
          <option value="">Select</option>
          {splitOptions(field.optionsText).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    const type = { number: "number", date: "date", datetime: "datetime-local" }[field?.type] ?? "text";
    return <input id={id} className="form-control" type={type} value={action.value ?? ""} onChange={onChange} placeholder="Value" />;
  };

  return (
    <div className="p-4 mb-3 cm-card">
      <Heading
        title="Buttons"
        hint={'Shown on the view page. A button sets one field to a fixed value, Ex: "Approve" sets Status to Approved, and hides itself once the record already has that value.'}
      />

      {draft.actions.map((action, index) => {
        const id = (name) => `action-${index}-${name}`;
        const color = ACTION_COLORS[action.color]?.color ?? ACTION_COLORS.primary.color;
        return (
          <div key={index} className="p-3 mb-3 cm-field-card">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fs-14 fw-600" style={{ background: color, color: "#fff", borderRadius: "8px", padding: "6px 14px" }}>
                {action.label || "Button"}
              </span>
              <DeleteRow
                style={{ cursor: "pointer" }}
                role="button"
                aria-label="Remove button"
                onClick={() => setDraft((current) => ({ ...current, actions: current.actions.filter((_, i) => i !== index) }))}
              />
            </div>
            <Row>
              <Col md={4} className="mb-2">
                <Label htmlFor={id("label")}>BUTTON LABEL *</Label>
                <input id={id("label")} className="form-control" maxLength={50} value={action.label} onChange={(e) => update(index, { label: e.target.value })} placeholder="Ex: Approve" />
              </Col>
              <Col md={4} className="mb-2">
                <Label htmlFor={id("field")}>FIELD TO UPDATE *</Label>
                <select
                  id={id("field")}
                  className="form-control"
                  value={action.field}
                  onChange={(e) => {
                    const target = draft.fields.find((field) => field.key === e.target.value);
                    update(index, { field: e.target.value, value: target?.type === "checkbox" ? false : "" });
                  }}
                >
                  <option value="">Select</option>
                  {targets.map((field) => (
                    <option key={field.key} value={field.key}>{labelOf(field)}</option>
                  ))}
                </select>
              </Col>
              <Col md={4} className="mb-2">
                <Label htmlFor={id("value")}>SET VALUE TO *</Label>
                {valueInput(action, index, id("value"))}
              </Col>
              <Col md={4} className="mb-2">
                <Label htmlFor={id("color")}>COLOUR</Label>
                <select id={id("color")} className="form-control" value={action.color} onChange={(e) => update(index, { color: e.target.value })}>
                  {Object.entries(ACTION_COLORS).map(([value, item]) => (
                    <option key={value} value={value}>{item.label}</option>
                  ))}
                </select>
              </Col>
              <Col md={8} className="mb-2">
                <Label htmlFor={id("confirm")}>CONFIRMATION MESSAGE</Label>
                <input
                  id={id("confirm")}
                  className="form-control"
                  maxLength={200}
                  value={action.confirm}
                  onChange={(e) => update(index, { confirm: e.target.value })}
                  placeholder="Optional, Ex: Approve this vendor?"
                />
              </Col>
              <Col md={12} className="mb-2">
                <Label htmlFor={id("condition")}>SHOW THIS BUTTON</Label>
                <ConditionEditor id={id("condition")} fields={controllers} rule={action.visible_when} onChange={(rule) => update(index, { visible_when: rule })} />
              </Col>
            </Row>
            <RowErrors errors={errors} prefix={`actions.${index}.`} />
          </div>
        );
      })}

      <button type="button" className="fs-14 fw-600 cm-pill" onClick={() => setDraft((current) => ({ ...current, actions: [...current.actions, emptyAction()] }))}>
        + Add button
      </button>
    </div>
  );
};
